import React from "react";
import { Table as BaseTable } from "react-bootstrap";
import styled, { css } from "styled-components";
import { IconButton } from "@blueprint/ui";


type HeaderCellComponentProps = {
    className?: string;
    width: number;
};
type BodyRowComponentProps = {
    hasAction?: boolean;
    plain?: boolean;
    onClick?: () => void;
    onKeyUp?: any;
};
type PaginationProps = {
    backArrowOnClick: () => void;
    backArrowDisabled?: boolean;
    nextArrowOnClick: () => void;
    nextArrowDisabled?: boolean;
    totalPage: number;
    currentPage: number;
    onPageNumberChange: (pageNumber: number) => void;
    className?: string;
};

type HeaderCellComponent = React.FunctionComponent<HeaderCellComponentProps>;
type BodyRowComponent = React.FunctionComponent<BodyRowComponentProps>;
type PaginationComponent = React.FunctionComponent<PaginationProps>;
type TableComponent = React.FunctionComponent & {
    HeaderCell: HeaderCellComponent;
    BodyRow: BodyRowComponent;
    Pagination: PaginationComponent;
};

const Table: TableComponent = (props): JSX.Element => (
    <StyledTable {...props} />
);

const StyledTable = styled(BaseTable)`
    table-layout: auto;
    border-collapse: separate;
    border-spacing: 0 0.5rem;

    th {
        font-size: 0.675rem;
        font-weight: normal;
        color: var(--bs-label-grey);
        padding-bottom: 0;
    }

    tbody {
        tr {
            background-color: var(--bs-background-grey);

            td {
                &:first-child {
                    border-top-left-radius: 8px;
                    border-bottom-left-radius: 8px;
                }

                &:last-child {
                    border-top-right-radius: 8px;
                    border-bottom-right-radius: 8px;
                }

                &.cta-cell {
                    position: relative;

                    .cta {
                        position: absolute;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%);
                        padding: 4px;
                        background-color: var(--bs-danger);
                        border-radius: 50%;
                    }
                }
            }
        }
    }
`;

const HeaderCell: HeaderCellComponent = styled.th<{ width: number }>`
    width: ${(props) => `${props.width}px`};
`;

const BodyRow: BodyRowComponent = (props): JSX.Element => {
    return <StyledTr {...props} tabIndex={0} />;
};

const StyledTr = styled.tr<{ hasAction?: boolean; plain?: boolean }>`
    ${(props) =>
        !props.plain &&
        css<{ hasAction?: boolean }>`
            cursor: pointer;
            transition: background-color 0.3s;
            :hover {
                background-color: ${(props) => "var(--bs-humble-grey)"};
            }

            &:active {
                background-color: #c5cdd0;
            }
        `}
`;

const Pagination: PaginationComponent = ({
    backArrowOnClick,
    backArrowDisabled,
    nextArrowOnClick,
    nextArrowDisabled,
    totalPage,
    currentPage,
    onPageNumberChange,
    className,
}): JSX.Element => {
    const inputOnBlurHandler = (
        event: React.FocusEvent<HTMLInputElement, Element>,
    ) => {
        const value = Number(event.target.value);
        if (value <= 0 || value > totalPage) {
            event.target.value = String(currentPage);
        } else {
            onPageNumberChange(value);
        }
    };
    var currentValue = currentPage + "";
    const inputOnChange = (
        event: React.FocusEvent<HTMLInputElement, Element>,
    ) => {
        event.target.value = event.target.value.replace(/\D/g,"");
        currentValue = event.target.value.replace(/\D/g,"");
    };
    return (
        <StyledPagination className={className}>
            <div className="pages">
                <input
                    className="text-dark"
                    defaultValue={currentPage}
                    onBlur={inputOnBlurHandler}
                    onChange={inputOnChange}
                    aria-label={`Go to page ${currentValue} of ${totalPage}`}
//                     value={currentPage}
                />
                <span>of {totalPage}</span>
            </div>
            <div>
                <IconButton
                    aria-label="previous page"
                    icon="ChevronLeft"
                    onClick={backArrowOnClick}
                    disabled={backArrowDisabled}
                    variant="light.tertiary"
                    style={{ marginRight: "10px" }}
                />
                <IconButton
                    aria-label="next page"
                    icon="ChevronRight"
                    onClick={nextArrowOnClick}
                    disabled={nextArrowDisabled}
                    variant="light.tertiary"
                />
            </div>
        </StyledPagination>
    );
};

const StyledPagination = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 24px;
    .arrows {
        width: 60px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        span {
            &.disabled {
                pointer-events: none;
                opacity: 0.2;
            }
            svg {
                cursor: pointer;
                padding: 4px;
                width: 24px;
                height: 24px;
            }
        }
    }
    .pages {
        margin-right: 15px;
        font-size: 0.75rem;
        input {
            height: 2.5rem;
            width: 2.5rem;
            border: 1px solid #767676;
            text-align: center;
            margin-right: 8px;
            outline: none;
            padding: 0.625rem;
            &:hover:not(:disabled),
            &:focus,
            &:active:not(:disabled) {
                border-color: #2278ce;
            }
        }
    }
`;

Table.HeaderCell = HeaderCell;
Table.BodyRow = BodyRow;
Table.Pagination = Pagination;

export default Table;
